/**
 * Same-origin API client. The session rides on the httpOnly cookie; no token is ever
 * readable here. Writes carry the session's CSRF token (the API requires it for
 * cookie-authenticated writes). A 401 goes to one app-wide handler (back to login).
 *
 * The ERP's gateway answers under /api/erp/ on a tenant's host (the edge, and Vite in dev, send
 * it there without the erp/), beside the platform's own /api/. Its paths are written as the
 * gateway serves them (/api/odoo/menus); erpUrl() puts them under /api/erp/.
 */
import { getCsrfToken } from './session'

/** A gateway path (`/api/…`) at its address on the tenant's host (`/api/erp/…`). Idempotent. */
export function erpUrl(path: string): string {
    if (path.startsWith('/api/erp/') || !path.startsWith('/api/')) return path
    return `/api/erp/${path.slice('/api/'.length)}`
}

export class ApiError extends Error {
    readonly status: number
    /** The response's machine-readable `code`, when its JSON body has one (e.g. `exists_here`). */
    readonly code: string | null
    /** The response's human-readable `message` (e.g. Odoo's UserError text), when it has one. */
    readonly detail: string | null

    constructor(status: number, message: string, code: string | null = null, detail: string | null = null) {
        super(message)
        this.name = 'ApiError'
        this.status = status
        this.code = code
        this.detail = detail
    }
}

let onUnauthorized: (() => void) | null = null

/** Registered once by the app; called on any 401 except where a request opts out. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
    onUnauthorized = handler
}

interface RequestOptions {
    signal?: AbortSignal
    /** Default true. Session probes (/auth/me) and login opt out: a 401 there is an answer, not an error. */
    handleUnauthorized?: boolean
}

async function request<T>(method: string, path: string, body: unknown, options: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    if (method !== 'GET') {
        const csrf = getCsrfToken()
        if (csrf) headers['X-CSRF-Token'] = csrf
    }

    const response = await fetch(erpUrl(path), {
        method,
        credentials: 'same-origin',
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: options.signal,
    })

    if (response.status === 401 && options.handleUnauthorized !== false) {
        onUnauthorized?.()
    }
    if (!response.ok) {
        const failure = (await response.json().catch(() => null)) as { code?: unknown; message?: unknown } | null
        const code = failure && typeof failure.code === 'string' ? failure.code : null
        const detail = failure && typeof failure.message === 'string' ? failure.message : null
        throw new ApiError(response.status, `${method} ${path} failed with HTTP ${response.status}`, code, detail)
    }
    if (response.status === 204) return undefined as T
    return (await response.json()) as T
}

export const api = {
    get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
    post: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('POST', path, body ?? {}, options),
    patch: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('PATCH', path, body ?? {}, options),
    delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, undefined, options),
}

/** Retry only what can succeed on retry: network failures and 5xx, at most twice. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
    if (failureCount >= 2) return false
    if (error instanceof ApiError) return error.status >= 500
    return !(error instanceof DOMException && error.name === 'AbortError')
}
